"use client";
import PreviewMode from "@components/cms/preview/PreviewMode";
import { GlobalModalProvider } from "@components/GlobalModalProvider/GlobalModalProvider";
import { AnalyticsProvider } from "@lib/analytics/AnalyticsProvider";
import { MicrocopyProvider } from "@lib/contentful/microcopy/MicrocopyContext";
import type { MicrocopySet } from "@lib/contentful/microcopy/types";
import dynamic from "next/dynamic";
import type { ComponentType } from "react";
import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import { isProduction } from "@config/site/helpers";

let Backstage: ComponentType | null = null;
if (!isProduction) {
    Backstage = dynamic(() => import("@lib/monitoring/site-info-tooltip/Backstage"), {
        // For some reason setting this to false breaks SSR
        ssr: true,
    });
}

interface PageContentProps {
    children: React.ReactNode;
    pageType: string;
    pageMicrocopySet?: MicrocopySet;
    isPreview?: boolean;
    microcopySets?: any;
    sizeGuideModal?: any;
}

export default function PageContent(props: PageContentProps) {
    const { siteSettings, sharedMicrocopySet } = useGlobalContent();

    const { children, pageMicrocopySet, microcopySets = {}, pageType, isPreview = false } = props;
    const fields = siteSettings?.fields;
    const {
        offerToLogIn,
        offerToCreateAnAccount,
        policiesAgreementTerms,
        authorizationInvitation,
        forgotYourPassword,
        contactFormAgreement,
        reCaptchaAgreement,
        shippingDetailsModal,
        profileLinkCollection,
        liveChatAgreement,
        liveChatOfflineDescription,
        giftWrappingDescription,
        eccoNewsletter,
        eccoMembership,
        sizeGuideModal,
    } = fields ?? {};

    const authorizationModules = {
        offerToLogIn,
        offerToCreateAnAccount,
        policiesAgreementTerms,
        authorizationInvitation,
        forgotYourPassword,
        eccoNewsletter,
        eccoMembership,
    };
    const contactFormModules = {
        contactFormAgreement,
        reCaptchaAgreement,
    };
    const liveChatModal = {
        liveChatAgreement,
        liveChatOfflineDescription,
    };

    return (
        <AnalyticsProvider pageType={pageType}>
            <MicrocopyProvider
                value={{
                    shared: sharedMicrocopySet,
                    page: pageMicrocopySet,
                    ...microcopySets,
                }}
            >
                <PreviewMode preview={isPreview} />
                <GlobalModalProvider
                    authorizationModules={authorizationModules}
                    linkCollection={profileLinkCollection}
                    sizeGuideModal={sizeGuideModal}
                    contactFormModules={contactFormModules}
                    shippingDetailsModal={shippingDetailsModal}
                    liveChatModal={liveChatModal}
                    giftWrappingModal={giftWrappingDescription}
                >
                    {Backstage && <Backstage />}
                    {children}
                </GlobalModalProvider>
            </MicrocopyProvider>
        </AnalyticsProvider>
    );
}
